import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'

import {
  BannerComponentOptionType,
  HomeTabComponentTypeOptionType,
} from 'features/MultiCategory/@types/homeTabComponent'
import { getImageRules } from 'utils/apiList/images'

import { getHomepageComponentKeyByCode } from 'features/MultiCategory/constant/susunanKomponen'
import { CATEGORY_SPECIFIC_TILE_IMAGE_VARIANT_ID } from 'features/MultiCategory/constant/homeTabCreateEdit'
import { parseComponentTypeMetadata } from 'features/MultiCategory/utils/componentTypeMetadata'
import { toHubNames } from 'features/MultiCategory/utils/componentOptionLocation'
import { mergeSchedules, toSchedules } from 'features/MultiCategory/utils/componentOptionSchedule'
import {
  BannerComponentOptionItemType,
  getBannerComponentOptions,
  getHomeTabComponentTypes,
} from 'features/MultiCategory/services/homeTabOptions'
import {
  SLICE_NAME,
  setBannerOptions,
  setComponentTypeOptions,
  setIsBannerOptionsLoading,
  setIsComponentTypeOptionsLoading,
  setTileImageRule,
  upsertSelectedBannerOptions,
} from './slice'

/** Rows collapse by banner id, gathering every hub they name. */
const groupBannerOptions = (items: BannerComponentOptionItemType[]) => {
  const groups: Record<number, BannerComponentOptionType> = {}

  items.forEach((item) => {
    const group = groups[item.id] || {
      id: item.id,
      name: item.name,
      targetingTypeName: item.targeting_type_name || '',
      schedule: [],
      hubNames: [],
    }

    mergeSchedules(group.schedule, toSchedules(item))

    toHubNames(item.location).forEach((hubName) => {
      if (!group.hubNames.includes(hubName)) {
        group.hubNames.push(hubName)
      }
    })

    groups[item.id] = group
  })

  return Object.values(groups)
}

/**
 * Resolves a component's `reference_ids` into full banners. The detail API
 * returns ids only, and the search-driven `bannerOptions` list rarely holds
 * them, so each id is looked up through the options API's `id` filter.
 */
export const fetchBannerComponentOptionsByIds = createAsyncThunk(
  `${SLICE_NAME}/fetchBannerComponentOptionsByIds`,
  async (ids: number[], { dispatch, getState }) => {
    const {
      multiCategoryHomeTabComponent: { selectedBannerOptions },
    } = getState() as StoreStateType

    const missingIds = ids.filter(
      (id) => id > 0 && !selectedBannerOptions.some((banner) => banner.id === id),
    )

    if (!missingIds.length) {
      return
    }

    try {
      const responses = await Promise.all(missingIds.map((id) => getBannerComponentOptions({ id })))

      const banners = responses.flatMap(({ data: { data } }) => groupBannerOptions(data || []))

      dispatch(upsertSelectedBannerOptions(banners))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

/** Search keyword; empty fetches every banner. */
export const fetchBannerComponentOptions = createAsyncThunk(
  `${SLICE_NAME}/fetchBannerComponentOptions`,
  async (name: string | void, { dispatch }) => {
    dispatch(setIsBannerOptionsLoading(true))

    try {
      const {
        data: { data },
      } = await getBannerComponentOptions(name ? { name } : {})

      dispatch(setBannerOptions(groupBannerOptions(data || [])))
    } catch (error) {
      callErrorMsg(error)
      dispatch(setBannerOptions([]))
    } finally {
      dispatch(setIsBannerOptionsLoading(false))
    }
  },
)

/**
 * Master list behind "Pilih Komponen Homepage". The API owns which components
 * exist and their display names; the drawer form is matched through `code`.
 */
export const fetchHomeTabComponentTypes = createAsyncThunk(
  `${SLICE_NAME}/fetchHomeTabComponentTypes`,
  async (_: void, { dispatch, getState }) => {
    const {
      multiCategoryHomeTabComponent: { componentTypeOptions },
    } = getState() as StoreStateType

    /* Master data — fetch once per page visit. */
    if (componentTypeOptions.length) {
      return
    }

    dispatch(setIsComponentTypeOptionsLoading(true))

    try {
      const {
        data: { data },
      } = await getHomeTabComponentTypes()

      const options: HomeTabComponentTypeOptionType[] = (data || []).map((item) => {
        const metadata = parseComponentTypeMetadata(item.metadata)

        return {
          id: item.id,
          code: item.code,
          name: item.name,
          key: getHomepageComponentKeyByCode(item.code),
          /* The flag moved to the row itself; metadata still answers for older rows. */
          isAllTabOnly: item.all_tab_only ?? metadata.isAllTabOnly,
          metadata,
        }
      })

      dispatch(setComponentTypeOptions(options))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setIsComponentTypeOptionsLoading(false))
    }
  },
)

/**
 * Upload rules for the Category Specific tiles. Every tile uploads to the same
 * bucket, so the drawer reads them once instead of once per tile.
 */
export const fetchTileImageRule = createAsyncThunk(
  `${SLICE_NAME}/fetchTileImageRule`,
  async (_: void, { dispatch, getState }) => {
    const {
      multiCategoryHomeTabComponent: { tileImageRule },
    } = getState() as StoreStateType

    if (tileImageRule) {
      return
    }

    try {
      const {
        data: { data },
      } = await getImageRules(CATEGORY_SPECIFIC_TILE_IMAGE_VARIANT_ID)

      dispatch(setTileImageRule(data))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)
